
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from './firebase';
import './index.css';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import UsersList from './components/UsersList';
import Campaigns from './components/Campaigns';
import Takeovers from './components/Takeovers';
import SpotlightsList from './components/SpotlightDetail';
import CreativesList from './components/CreativesList';
import SignsConverter from './components/SignsConverter';
import SignsList from './components/SignsList';
import Layout from './components/Layout';
import CampaignDetailPage from './components/CampaignDetailPage';
import TakeoverDetailPage from './components/TakeoverDetailPage';
import Analytics from './components/Analytics';
import IMessageThreads from './components/iMessageThreads';
import CouponsPage from './components/CouponsPage';
import Finances from './components/Finances';
import Organizations from './components/Organizations';
import FeaturedSignsCoverageMap from './components/FeaturedSignsCoverageMap';
import UGCSubmissions from './components/UGCSubmissions';
import ScheduledPushPage from './components/ScheduledPushPage';

// Verify the shared session cookie once per page load, then sign the Firebase
// client SDK in with a custom token so all Firestore queries work normally.
// Returns 'ok' | 'blocked' | 'unauthenticated'
let sessionPromise = null;
const checkSession = () => {
  if (!sessionPromise) {
    sessionPromise = fetch('/api/session/check', { credentials: 'same-origin' })
      .then(async (res) => {
        if (res.status === 403) return 'blocked';
        if (!res.ok) return 'unauthenticated';
        const { customToken } = await res.json();
        if (customToken) {
          await signInWithCustomToken(auth, customToken);
        }
        return 'ok';
      })
      .catch(() => 'unauthenticated');
  }
  return sessionPromise;
};

const ProtectedRoute = ({ element }) => {
  const [authState, setAuthState] = useState('loading');

  useEffect(() => {
    checkSession().then((result) => {
      setAuthState(result);
      if (result === 'unauthenticated' || result === 'blocked') {
        const returnTo = encodeURIComponent(window.location.href);
        window.location.replace(`https://sso.vuebillboards.com?returnTo=${returnTo}`);
      }
    });
  }, []);

  if (authState === 'loading' || authState === 'unauthenticated' || authState === 'blocked') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-100 px-6">
        <div className="ms-dots py-0">
          <span className="ms-dot" />
          <span className="ms-dot" />
          <span className="ms-dot" />
        </div>
        <p className="text-sm font-medium text-slate-500">Checking session…</p>
      </div>
    );
  }

  return element;
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Router>
    <Routes>
      {/* Public routes for login */}
      <Route path="/login" element={<Login />} />

      {/* Protected routes with Layout */}
      <Route path="/" element={<ProtectedRoute element={<Layout><Dashboard /></Layout>} />} />
      <Route path="/dashboard" element={<ProtectedRoute element={<Layout><Dashboard /></Layout>} />} />
      <Route path="/analytics" element={<ProtectedRoute element={<Layout><Analytics /></Layout>} />} />
      <Route path="/users" element={<ProtectedRoute element={<Layout><UsersList /></Layout>} />} />
      <Route path="/organizations" element={<ProtectedRoute element={<Layout><Organizations /></Layout>} />} />
      <Route path="/campaigns" element={<ProtectedRoute element={<Layout><Campaigns /></Layout>} />} />
      <Route path="/campaigns/:id" element={<ProtectedRoute element={<Layout><CampaignDetailPage /></Layout>} />} />
      <Route path="/takeovers" element={<ProtectedRoute element={<Layout><Takeovers /></Layout>} />} />
      <Route path="/takeovers/:id" element={<ProtectedRoute element={<Layout><TakeoverDetailPage /></Layout>} />} />
      <Route path="/spotlights" element={<ProtectedRoute element={<Layout><SpotlightsList /></Layout>} />} />
      <Route path="/creatives" element={<ProtectedRoute element={<Layout><CreativesList /></Layout>} />} />
      <Route path="/signs" element={<ProtectedRoute element={<Layout><SignsConverter /></Layout>} />} />
      <Route path="/signs-list" element={<ProtectedRoute element={<Layout><SignsList /></Layout>} />} />
      <Route path="/imessage" element={<ProtectedRoute element={<Layout><IMessageThreads /></Layout>} />} />
      <Route path="/coupons" element={<ProtectedRoute element={<Layout><CouponsPage /></Layout>} />} />
      <Route path="/ugc" element={<ProtectedRoute element={<Layout><UGCSubmissions /></Layout>} />} />
      <Route path="/scheduled-push" element={<ProtectedRoute element={<Layout><ScheduledPushPage /></Layout>} />} />
      <Route path="/finances" element={<ProtectedRoute element={<Layout><Finances /></Layout>} />} />
      <Route
        path="/featured-signs-coverage"
        element={<ProtectedRoute element={<Layout><FeaturedSignsCoverageMap /></Layout>} />}
      />

      {/* Catch-all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Router>
);

